import {Like} from "../models/customer";
import {Variant} from "../models/product";
import {Dimension, NY_Tracker, TrackingEvent} from "./tracking-service";
import {Customer, WebSiteCustomerService} from "./web-site-customer-service";
import {productService} from "./product-service";
import {FilterMap} from "../models/filter";


export class CustomerService {
  readonly CUSTOMER_SERVICE = 'NY_CUSTOMER_SERVICE';
  readonly TAG = 'NY-PRODUCTS-CLIENT';

  customerService: WebSiteCustomerService = null;
  dataSubscribers = [];
  customerData: Customer = null;
  customerNA = false;
  customerDataOnceSubscriber = [];

  likes: Like[] = [];
  likesLoaded: boolean = false;

  isLoggedIn: boolean = false;
  cachedVariant: Variant;
  alreadyLiked: boolean = false;

  constructor() {
    if (window && window[this.CUSTOMER_SERVICE]) {
      this.customerService = window[this.CUSTOMER_SERVICE];
      // noinspection JSUnresolvedFunction
      this.customerService.subscribeForCustomer(this.TAG, (customer: Customer) => {
        this.customerData = customer;
        this.isLoggedIn = !!customer;
        this.customerNA = false;
        this.notifyCustomerSubscribers(customer);
      });

      this.customerService.subscribeForCustomerNotAvailable(this.TAG, () => {
        this.customerNA = true;

      })
    }
  }

  subscribeForCustomer(callback) {
    this.dataSubscribers.push(callback);
    if (this.customerData || this.customerNA) {
      callback(this.customerData);
    }
  }

  subscribeForCustomerOnce() {
    if (this.customerData || this.customerNA) {
      return Promise.resolve(this.customerData)
    } else {
      let executor = (resolve) => {
        this.customerDataOnceSubscriber.push(resolve);
      };
      return new Promise(executor);
    }
  }

  notifyCustomerSubscribers(customer: Customer) {
    this.dataSubscribers.forEach(callback => {
      callback(customer);
    });

    this.customerDataOnceSubscriber.forEach(callback => {
      callback(customer);
    });
  }

  async toggleLike(variant: Variant, newLikeState: boolean, dimension: string): Promise<void> {
    let result: boolean = false;
    const event = {
      label: `${variant.productId} - ${variant.id}`,
      action: '',
      category: 'Products',
      likeOrigin: dimension
    };

    if (newLikeState) {
      result = await this.customerService.likeProduct(variant.productId, variant.id);
      event.action = 'productLiked';
    } else {
      result = await this.customerService.dislikeProduct(variant.productId, variant.id);
      event.action = 'productDisliked';
    }
    NY_Tracker.trackClick(event as TrackingEvent);

    if (result) {
      return;
    } else {
      throw new Error("[product-client] error while liking product");
    }
  };

  async loadLikesForUser(): Promise<Like[]> {
    if (this.likesLoaded) {
      return this.likes;
    }

    try {
      const response = await this.customerService.getLikesForCustomer();
      if (response) {
        this.likes = response.map(it => {
          let like = it.itemId.split('_');
          return new Like(like[0], like[1]);
        });
        this.likesLoaded = true;
        return this.likes;
      }
    } catch (err) {
      console.error("Error while loading likes: ", err);
    }
    return [];
  };

  async loadFavouriteProducts(countryCode: string) {
    const userLikes = await this.loadLikesForUser();

    this.cachedVariant = this.getCachedVariant()?.variant;
    this.alreadyLiked = this.cachedVariant ? this.isVariantLiked(this.cachedVariant, userLikes) : false;
    if (this.cachedVariant) {
      userLikes.push(new Like(this.cachedVariant.productId, this.cachedVariant.id));
    }

    if (userLikes.length <= 0) {
      return
    }

    const products = await productService.loadProductsForLikes(userLikes, countryCode);
    return {userLikes: userLikes, productItems: products?.items}
  }

  async likeCachedVariant() {
    if (this.cachedVariant && !this.alreadyLiked) {
      await this.toggleLike(this.cachedVariant, true, Dimension.LIST);
      this.removeCachedVariant();
    }
  }

  setCachedVariant(variant: Variant) {
    sessionStorage.setItem("LIKED_PRODUCT", JSON.stringify({"variant": variant}))
  }

  getCachedVariant() {
    const likedProduct = sessionStorage.getItem("LIKED_PRODUCT");
    return likedProduct ? JSON.parse(likedProduct) : likedProduct;
  }

  removeCachedVariant() {
    sessionStorage.removeItem("LIKED_PRODUCT");
  }

  isVariantLiked(variant: Variant, likes: Like[]) {
    const likeFound = likes.find(like =>
      like.productId === variant.productId && like.variantId === variant.id
    );

    return likeFound != null;
  }

  /**
   * update like cache
   * @param likes
   */
  setLikes(likes: Like[]) {
    this.likes = likes;
  }
}

export const customerService = new CustomerService();